import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { Sparkles, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/auth';
import PublicFooter from '@/components/PublicFooter';

export default function PartnerLogin() {
  const { user, login, loading } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);

  // All hooks must be declared BEFORE any conditional early return.
  // Previously `useMemo`/`useCallback` lived after `if (user) return <Navigate>`,
  // which broke the Rules of Hooks and triggered React concurrent-rendering
  // crashes after sign-in.
  const partnerHome = useMemo(() => '/partner/dashboard', []);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      setError(null);
      const result = await login(email.trim().toLowerCase(), password);
      if (!result.ok) {
        setError(result.error || 'Login failed');
        return;
      }
      // Wait one tick for /auth/me to settle; AuthProvider has the user now.
      navigate(partnerHome, { replace: true });
    },
    [email, password, login, navigate, partnerHome],
  );

  // If already logged in as partner, jump to dashboard
  if (user && user.role === 'partner') {
    return <Navigate to={partnerHome} replace />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-900 to-slate-800 flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center mb-6">
          <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-rose-500 to-rose-600 flex items-center justify-center shadow-lg">
            <Sparkles className="h-7 w-7 text-white" />
          </div>
          <h1 className="text-white text-2xl font-bold mt-3">BeautyMateUK</h1>
          <p className="text-rose-200 text-sm">Partner App</p>
        </div>

        <Card className="border-slate-200 shadow-xl">
          <CardHeader className="text-center space-y-1">
            <CardTitle className="text-xl">Welcome back</CardTitle>
            <CardDescription>Sign in to manage your bookings</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="p-email">Email</Label>
                <Input
                  id="p-email"
                  type="email"
                  data-testid="partner-login-email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-12"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="p-password">Password</Label>
                <Input
                  id="p-password"
                  type="password"
                  data-testid="partner-login-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-12"
                  required
                />
              </div>
              {error && (
                <div data-testid="partner-login-error" className="text-sm rounded-md bg-rose-50 border border-rose-200 text-rose-700 px-3 py-2">
                  {error}
                </div>
              )}
              <Button
                type="submit"
                disabled={loading}
                data-testid="partner-login-submit"
                className="w-full h-12 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-base"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Signing in...
                  </>
                ) : (
                  'Sign in'
                )}
              </Button>
              <p className="text-center text-sm text-slate-600 pt-2">
                New to BeautyMateUK?{' '}
                <button
                  type="button"
                  onClick={() => navigate('/partner/signup')}
                  className="text-rose-600 font-medium hover:underline"
                  data-testid="partner-login-to-signup"
                >
                  Become a Partner
                </button>
              </p>
            </form>
          </CardContent>
        </Card>
        <PublicFooter variant="minimal" />
      </div>
    </div>
  );
}
